import mqtt from 'mqtt';
import axios from 'axios';
import EventEmitter from 'eventemitter3';
import EncryptionDecryption from './encryptiondecryption.js';
import {
	SubscribeFeedResponse,
	Result,
	UnSubscribeFeedResponse,
	ConnectionResponse,
	DisconnectionResponse,
} from './models.js';

/**
 * @class Connect
 * @extends {EventEmitter}
 */

export default class Connect extends EventEmitter {
	static instance = null;
	client = null;
	_isConnected = false;

	//static Topics
	static _mw = 'prod/marketfeed/mw/v1/';
	static _index = 'prod/marketfeed/index/v1/';
	static _oi = 'prod/marketfeed/oi/v1/';
	static _marketStatus = 'prod/marketfeed/marketStatus/v1/';
	static _lpp = 'prod/marketfeed/lpp/v1/';
	static _high52Week = 'prod/marketfeed/high52week/v1/';
	static _low52Week = 'prod/marketfeed/low52week/v1/';
	static _upperCircuit = 'prod/marketfeed/uppercircuit/v1/';
	static _lowerCircuit = 'prod/marketfeed/lowercircuit/v1/';
	static _order = 'prod/updates/order/v1/';
	static _trade = 'prod/updates/trade/v1/';
	static _validateTokenUrl = '/api/idaas/access/check/token';

	constructor() {
		if (Connect.instance) {
			throw new Error('Use Connect.getInstance() instead of new Connect()');
		}
		super();
	}

	static getInstance() {
		if (!Connect.instance) {
			Connect.instance = new Connect();
			this.crypto = new EncryptionDecryption();
		}
		return Connect.instance;
	}

	//#region Property

	get isConnected() {
		return this._isConnected;
	}

	set isConnected(value) {
		this._isConnected = value;
	}

	//#endregion

	/**
	 * Connect to the MQTT host
	 * @param {Object} req - The request object containing connection details
	 * @param {Function} callback - The callback function to handle disconnection events
	 * @returns {Promise} - The connection result
	 */
	async connectHost(req, callback) {
		return new Promise(async (resolve, reject) => {
			try {
				if (!req) {
					return reject(JSON.stringify(new ConnectionResponse(101, 'Request cannot be Null')));
				}

				let jObject;
				try {
					jObject = JSON.parse(req);
				} catch (error) {
					return reject(JSON.stringify(new ConnectionResponse(101, 'Invalid JSON format')));
				}

				if (!jObject.token) {
					return reject(
						JSON.stringify(new ConnectionResponse(101, "The parameter 'token' should not be empty")),
					);
				}
				if (!jObject.host) {
					return reject(
						JSON.stringify(new ConnectionResponse(101, "The parameter 'host' should not be empty")),
					);
				}
				if (!jObject.port) {
					return reject(
						JSON.stringify(new ConnectionResponse(101, "The parameter 'port' should not be empty")),
					);
				}
				const username = Connect.getUserName(jObject.token);
				const validateRes = await Connect.ValidateTokenAsync(username, jObject.token);

				if (validateRes.status !== 'Success') {
					console.log(validateRes);
					return reject(JSON.stringify(new ConnectionResponse(validateRes.status, validateRes.message)));
				}
				const timestamp = Connect.getFormattedTimestamp(); // ddmmyyHHMMSS
				let { token, host, port } = jObject;

				// const createWebsocket = (url, websocketSubProtocols, options) => {
				//   const subProtocols = [
				//     websocketSubProtocols[0], // Default MQTT subprotocol
				//     token, // Custom authentication token
				//   ];
				//   return new WebSocket(url, subProtocols);
				// };

				this.client = mqtt.connect('mqtts://' + host, {
					clientId: `${username}_node_${timestamp}`,
					username: username,
					password: 'OPENID~~' + token + '~',
					port,
					rejectUnauthorized: false,
					clean: true,
					reconnectPeriod: 0, // No reconnection
					keepalive: 20,
					// createWebsocket: createWebsocket,
					// protocolVersion: 5,
				});
				// Handle Connection
				try {
					this.client.on('connect', () => {
						this.isConnected = true;
						resolve(JSON.stringify(new ConnectionResponse(0, 'Success')));
					});
				} catch (error) {
					if (error instanceof mqtt.MqttConnectingFailedException) {
						reject(new ConnectionResponse(error.resultCode, error.message));
					} else if (error instanceof InvalidOperationException) {
						reject(new ConnectionResponse(-1, error.message));
					} else if (error instanceof mqtt.MqttCommunicationException) {
						const errorCodeProperty = error.innerException?.ErrorCode;
						const errorCode = errorCodeProperty ? errorCodeProperty : -1;
						reject(new ConnectionResponse(errorCode, error.message));
					} else {
						reject(new ConnectionResponse(-1, error.message));
					}
				}

				this.client.on('error', (err) => {
					reject(JSON.stringify(new ConnectionResponse(err.errno, `${err.message}`)));
				});

				// Handle incoming messages
				this.client.on('message', async (topic, message) => {
					this.handleReceivedMessage(topic, message);
				});

				// Handle Disconnection
				this.client.on('disconnect', (packet) => {
					let status = packet?.reasonCode || 0;
					let message = packet?.properties?.reasonString || 'Disconnected from broker';

					callback(JSON.stringify({ status, message }));
				});

				this.client.on('close', () => {
					callback(
						JSON.stringify({
							status: 0,
							message: this.lastDisconnectReason || 'Connection Closed',
						}),
					);
				});

				this.client.on('offline', () => {
					callback(
						JSON.stringify({
							status: 0,
							message: this.lastDisconnectReason || 'Broker is Offline',
						}),
					);
				});
			} catch (error) {
				this.emit('OnError', -1, error.message);
				const response = new ConnectionResponse(-1, error.message);
				reject(JSON.stringify(response));
			}
		});
	}

	/**
	 * Extract username from the token
	 * @param {string} token - The JWT token
	 * @returns {string} - The username
	 */
	static getUserName(token) {
		try {
			const tokenParts = token.split('.');

			let payload = tokenParts[1];
			payload = atob(payload);
			const jwtPayload = JSON.parse(payload);
			return jwtPayload['preferred_username'] || 'tester';
		} catch (error) {
			return 'tester';
		}
	}

	/**
	 * Validate the token asynchronously
	 * @param {string} clientID - The client ID
	 * @param {string} token - The JWT token
	 * @returns {Object} - The validation result
	 */
	static async ValidateTokenAsync(clientID, token) {
		try {
			await this.crypto.init();
			const jReq = {
				userId: clientID,
				token: token,
			};
			const plainText = `{ "userId":"${clientID}","token":"${token}" }`;

			const encrypted = await this.crypto.Encrypt(plainText);

			const reqPayload = JSON.stringify({ cEncData: encrypted });

			// const agent = new https.Agent({
			//       rejectUnauthorized: false  // ⚠️ only for testing, disables SSL verification
			//       });

			const response = await axios.post(Connect._validateTokenUrl, reqPayload, {
				headers: { 'Content-Type': 'application/json' },
				// httpsAgent: agent
			});

			// Call decrypt
			const decrypted = await this.crypto.Decrypt(response.data.cRespEncData);

			const resData = JSON.parse(decrypted);

			if (response.status === 200) {
				return resData.result;
			} else {
				return [resData.result];
			}
		} catch (error) {
			if (error.response) {
				return { status: 'failed', message: error.response.data };
			}
			return { status: 'failed', message: error.message };
		}
	}

	/**
	 * Get formatted timestamp
	 * @returns {string} - The formatted timestamp
	 */
	static getFormattedTimestamp() {
		return new Date()
			.toLocaleString('en-GB', {
				day: '2-digit',
				month: '2-digit',
				year: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				hour12: false,
			})
			.replace(/[\/,:\s]/g, '');
	}

	//#region Subscribe

	async SubscribeFeed(req) {
		return await this.Subscribe(req, Connect._mw);
	}
	async SubscribeIndex(req) {
		return await this.Subscribe(req, Connect._index);
	}
	async SubscribeOpenInterest(req) {
		return await this.Subscribe(req, Connect._oi);
	}
	async SubscribeMarketStatus(req) {
		return await this.Subscribe(req, Connect._marketStatus);
	}
	async SubscribeLpp(req) {
		return await this.Subscribe(req, Connect._lpp);
	}
	async SubscribeHigh52week(req) {
		return await this.Subscribe(req, Connect._high52Week);
	}
	async SubscribeLow52week(req) {
		return await this.Subscribe(req, Connect._low52Week);
	}
	async SubscribeUpperCircuit(req) {
		return await this.Subscribe(req, Connect._upperCircuit);
	}
	async SubscribeLowerCircuit(req) {
		return await this.Subscribe(req, Connect._lowerCircuit);
	}
	async SubscribeOrderUpdates(req) {
		return await this.Subscribe(req, Connect._order);
	}
	async SubscribeTradeUpdates(req) {
		return await this.Subscribe(req, Connect._trade);
	}

	/**
	 * Subscribe to a topic
	 * @param {Object} req - The request object containing subscription details
	 * @param {string} topic - The topic to subscribe to
	 * @returns {Promise} - The subscription result
	 */
	async Subscribe(req, topic) {
		return new Promise((resolve, reject) => {
			try {
				if (!this.client) {
					return reject(JSON.stringify(new ConnectionResponse(105, 'Client is not connected', [])));
				}
				if (!req) {
					return reject(JSON.stringify(new SubscribeFeedResponse(101, 'Request cannot be Null', [])));
				}

				let subReq = JSON.parse(req);
				if (!subReq.subscriptionList || subReq.subscriptionList.length > 1024) {
					return reject(
						JSON.stringify(
							new SubscribeFeedResponse(
								102,
								'subscriptionList cannot be Null and no.of topics should be less than 1024',
								[],
							),
						),
					);
				}

				let topicRes = [];
				let topicCount = { value: 0 };
				let topicArray = []; // Array to store topics

				// Build the topic array
				Connect.BuildTopicArray(subReq.subscriptionList, topic, topicRes, topicCount, topicArray);

				// Subscribe only if there are valid topics
				if (topicArray.length > 0) {
					this.client.subscribe(topicArray, (err, granted) => {
						if (err) {
							reject(
								JSON.stringify(
									new SubscribeFeedResponse(103, `Subscription failed: ${err.message}`, []),
								),
							);
						}

						granted.forEach((item) => {
							const topicParts = item.topic.split('v1/');
							const extractedTopic = topicParts[1];
							if (item.qos === 128) {
								// QoS 128 means subscription failure for this topic
								topicRes.push(new Result(128, 'Failure', extractedTopic));
							} else {
								topicRes.push(new Result(0, 'Success', extractedTopic));
							}
						});

						resolve(JSON.stringify(new SubscribeFeedResponse(0, 'Success', topicRes)));
					});
				} else {
					reject(JSON.stringify(new SubscribeFeedResponse(103, 'No topics to subscribe', topicRes)));
				}
			} catch (error) {
				reject(JSON.stringify(new SubscribeFeedResponse(-1, error.message, [])));
			}
		});
	}

	//#endregion

	//#region Unsubscribe

	async UnSubscribeFeed(req) {
		return await this.UnSubscribe(req, Connect._mw);
	}
	async UnSubscribeIndex(req) {
		return await this.UnSubscribe(req, Connect._index);
	}
	async UnSubscribeOpenInterest(req) {
		return await this.UnSubscribe(req, Connect._oi);
	}
	async UnSubscribeMarketStatus(req) {
		return await this.UnSubscribe(req, Connect._marketStatus);
	}
	async UnSubscribeLpp(req) {
		return await this.UnSubscribe(req, Connect._lpp);
	}
	async UnSubscribeHigh52week(req) {
		return await this.UnSubscribe(req, Connect._high52Week);
	}
	async UnSubscribeLow52week(req) {
		return await this.UnSubscribe(req, Connect._low52Week);
	}
	async UnSubscribeUpperCircuit(req) {
		return await this.UnSubscribe(req, Connect._upperCircuit);
	}
	async UnSubscribeLowerCircuit(req) {
		return await this.UnSubscribe(req, Connect._lowerCircuit);
	}
	async UnSubscribeOrderUpdates(req) {
		return await this.UnSubscribe(req, Connect._order);
	}
	async UnSubscribeTradeUpdates(req) {
		return await this.UnSubscribe(req, Connect._trade);
	}

	/**
	 * Unsubscribe from a topic
	 * @param {Object} req - The request object containing unsubscription details
	 * @param {string} topic - The topic to unsubscribe from
	 * @returns {Promise} - The unsubscription result
	 */
	async UnSubscribe(req, topic) {
		return new Promise((resolve, reject) => {
			try {
				if (!this.client) {
					throw JSON.stringify(new ConnectionResponse(105, 'Client is not connected'));
				}
				if (!req) {
					throw JSON.stringify(new UnSubscribeFeedResponse(101, 'Request cannot be Null'));
				}

				let subReq = JSON.parse(req);
				if (!subReq.unSubscriptionList || subReq.unSubscriptionList.length > 1024) {
					return reject(
						JSON.stringify(
							new UnSubscribeFeedResponse(
								102,
								'UnsubscriptionList cannot be Null and no.of topics should be less than 1024',
							),
						),
					);
				}

				let topicRes = [];
				let topicCount = { value: 0 };
				let topicArray = []; // Array to store topics

				// Build the topic array
				Connect.BuildTopicArray(subReq.unSubscriptionList, topic, topicRes, topicCount, topicArray);

				// UnSubscribe only if there are valid topics
				if (topicArray.length > 0) {
					this.client.unsubscribe(topicArray, (err, granted) => {
						if (err) {
							return reject(
								JSON.stringify(
									new UnSubscribeFeedResponse(103, `UnSubscription failed: ${err.message}`),
								),
							);
						}

						const topicRes = topicArray.map((topic) => {
							const topicParts = topic.split('v1/');
							const extractedTopic = topicParts[1];
							return new Result(0, 'Unsubscribed', extractedTopic);
						});
						resolve(JSON.stringify(new UnSubscribeFeedResponse(0, 'Success', topicRes)));
					});
				} else {
					reject(JSON.stringify(new UnSubscribeFeedResponse(103, 'No topics to Unsubscribe', topicRes)));
				}
			} catch (error) {
				reject(
					JSON.stringify(
						error instanceof UnSubscribeFeedResponse
							? error
							: new UnSubscribeFeedResponse(-1, error.message.toString()),
					),
				);
			}
		});
	}

	//#endregion

	/**
	 * Build the topic array for subscription/unsubscription
	 * @param {Array} topicList - List of topics
	 * @param {string} topicPrefix - The topic prefix
	 * @param {Array} topicRes - The result array to store topic results
	 * @param {Object} topicCount - The topic count object
	 * @param {Array} topicArray - The topic array to store valid topics
	 */
	static BuildTopicArray(topicList, topicPrefix, topicRes, topicCount, topicArray) {
		try {
			topicList.forEach((topic) => {
				if (!/^[0-9a-z\/]+$/.test(topic)) {
					topicRes.push((104, 'Invalid topic', topic));
				} else {
					topicCount.value++;
					topicArray.push(`${topicPrefix}${topic}`); // Adding the topic to the array
				}
			});
		} catch (error) {
			this.emit('OnError', -1, error.message);
		}
	}

	/**
	 * Handle received messages from the MQTT broker
	 * @param {string} topic - The topic of the message
	 * @param {Buffer} message - The message payload
	 */
	async handleReceivedMessage(topic, message) {
		try {
			const topicParts = topic.split('/v1/');
			const topicPrefix = topicParts[0];
			const subTopic = topicParts[1];

			switch (topicPrefix) {
				case 'prod/marketfeed/mw':
					this.emit('OnFeedDataReceived', message, subTopic);
					break;
				case 'prod/marketfeed/index':
					this.emit('OnIndexDataReceived', message, subTopic);
					break;
				case 'prod/marketfeed/oi':
					this.emit('OnOpenInterestDataReceived', message, subTopic);
					break;
				case 'prod/marketfeed/marketstatus':
					this.emit('OnMarketStatusDataReceived', message, subTopic);
					break;
				case 'prod/marketfeed/lpp':
					this.emit('OnLppDataReceived', message, subTopic);
					break;
				case 'prod/marketfeed/high52week':
					this.emit('OnHigh52WeekDataReceived', message, subTopic);
					break;
				case 'prod/marketfeed/low52week':
					this.emit('OnLow52WeekDataReceived', message, subTopic);
					break;
				case 'prod/marketfeed/uppercircuit':
					this.emit('OnUpperCircuitDataReceived', message, subTopic);
					break;
				case 'prod/marketfeed/lowercircuit':
					this.emit('OnLowerCircuitDataReceived', message, subTopic);
					break;
				case 'prod/updates/order':
					this.emit('OnOrderUpdatesReceived', message, subTopic);
					break;
				case 'prod/updates/trade':
					this.emit('OnTradeUpdatesReceived', message, subTopic);
					break;
			}
		} catch (error) {
			this.emit('OnError', -1, error.message);
		}
	}

	/**
	 * Disconnect from the MQTT host
	 * @returns {Promise} - The disconnection result
	 */
	async DisconnectHost() {
		return new Promise((resolve, reject) => {
			try {
				if (!this.client) {
					return resolve(JSON.stringify(new DisconnectionResponse(0, 'Failure')));
				}

				this.client.end(false, () => {
					if (this.client.connected) {
						resolve(JSON.stringify(new DisconnectionResponse(0, 'Failure')));
					} else {
						this.client.removeAllListeners();
						this.client = null;
						this.isConnected = false;
						Connect.instance = null;
						resolve(JSON.stringify(new DisconnectionResponse(0, 'Success')));
					}
				});
			} catch (error) {
				if (this.onError) {
					this.emit('OnError', -1, error.message);
				}
				reject(JSON.stringify(new DisconnectionResponse(0, 'Failure')));
			}
		});
	}
}

// window.Connect = Connect;
